import { useState, useEffect, useCallback } from "react";
import { api, formatApiError } from "@/lib/api";
import { BadgeCheck, Clock, XCircle } from "lucide-react";

const STATUS_UI = {
  pending: { label: "En attente", cls: "bg-amber-50 text-amber-700", Icon: Clock },
  verified: { label: "Vérifié", cls: "bg-green-50 text-green-700", Icon: BadgeCheck },
  rejected: { label: "Refusé", cls: "bg-red-50 text-red-600", Icon: XCircle },
};

export default function VerificationsSection() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ type: "skill", subject: "", evidence: "" });
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/verifications/mine");
      setItems(res.data);
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setMessage("");
    setSubmitting(true);
    try {
      await api.post("/verifications/requests", form);
      setForm({ type: "skill", subject: "", evidence: "" });
      setMessage("Demande de vérification envoyée. L'équipe NEXA l'examinera.");
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-turquoise focus:border-transparent";

  return (
    <div data-testid="verifications-section">
      <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
        <BadgeCheck className="w-4 h-4 text-brand-turquoise" />
        Vérifications ({items.length})
      </h2>

      <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 p-4 mb-4 space-y-3">
        {error && <div data-testid="verification-form-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-2">{error}</div>}
        {message && <div data-testid="verification-form-success" className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-4 py-2">{message}</div>}
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="verification-type-select" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className={`${inputCls} bg-white`}>
            <option value="skill">Compétence</option>
            <option value="experience">Expérience</option>
          </select>
          <input data-testid="verification-subject-input" required value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} placeholder="Sujet (ex. Soudage TIG)" className={inputCls} />
        </div>
        <input data-testid="verification-evidence-input" value={form.evidence} onChange={(e) => setForm({ ...form, evidence: e.target.value })} placeholder="Preuve — lien https:// (certificat, portfolio…)" className={inputCls} />
        <button
          data-testid="verification-submit-button"
          type="submit"
          disabled={submitting}
          className="w-full bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-4 py-2.5 hover:bg-brand-navy transition-colors duration-200 disabled:opacity-50"
        >
          {submitting ? "Envoi…" : "Demander une vérification"}
        </button>
      </form>

      <div className="space-y-2">
        {items.map((v) => {
          const ui = STATUS_UI[v.status] || STATUS_UI.pending;
          return (
            <div key={v.verification_id} data-testid={`verification-${v.verification_id}`} className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-sm font-medium text-brand-navy truncate">
                  {v.type === "skill" ? "Compétence" : "Expérience"} : {v.subject}
                </p>
                <p className="text-xs text-slate-400">
                  {v.created_at ? new Date(v.created_at).toLocaleDateString("fr-CA") : "—"}
                </p>
              </div>
              <span data-testid={`verification-status-${v.verification_id}`} className={`text-xs font-medium rounded-full px-3 py-1 shrink-0 flex items-center gap-1 ${ui.cls}`}>
                <ui.Icon className="w-3.5 h-3.5" />
                {ui.label}
              </span>
            </div>
          );
        })}
        {items.length === 0 && (
          <p data-testid="verifications-empty" className="text-sm text-slate-400 bg-white rounded-xl border border-slate-200 p-6 text-center">
            Aucune vérification. Prouvez vos compétences pour renforcer votre réputation NEXA.
          </p>
        )}
      </div>
    </div>
  );
}
